'use client';

import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { zodResolver } from '@hookform/resolvers/zod';
import { Label } from '@packages/shared/src/ui/Label/Label';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { type MessageDescriptor, useIntl } from 'react-intl';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Dialog, Link } from '@packages/shared';
import { useState } from 'react';
import { isReactNodeString } from '../../utils/typeguards';
import { LivingSituationCard } from './LivingSituationCard';
import { AdditionalCostsCard } from './AdditionalCostsCard';

import { FurtherInformationCard } from './FurtherInformationCard';
import {
  creditCheckFormSchema,
  type CreditCheckFormValues,
  creditCheckWontDiscloseInformationSchema,
  type FormatMessageType,
  type TiltFormSchema,
} from './schema';
import { ShortFormSection } from './ShortFormSection';
import { LongFormSection } from './LongFormSection';
import type { OpenDialogHandler } from './types';
import { creditFormMessages as messages } from './messages';

type CreditCheckFormProps = {
  kind: 'long' | 'short';
  returnUrl: string;
};

type DialogState = {
  dialogTitle: ReturnType<FormatMessageType>;
  dialogContent: ReturnType<FormatMessageType>;
  open: boolean;
  showButtons?: boolean;
};
const RequiredHintParagraph: React.FC = () => {
  const { formatMessage } = useIntl();
  return (
    <p>
      <span className="text-size-body2">{formatMessage(messages.requiredHint)}</span>
    </p>
  );
};
export const CreditCheckForm: React.FC<CreditCheckFormProps> = ({ kind, returnUrl }) => {
  const { formatMessage } = useIntl();
  const [formStep, setFormStep] = useState(1);
  const [dialogState, setDialogState] = useState<DialogState>({
    dialogTitle: '',
    dialogContent: '',
    open: false,
    showButtons: false,
  });

  const { trigger, control, setValue, formState, getValues, handleSubmit, ...restMethods } =
    useForm<CreditCheckFormValues<typeof kind>>({
      resolver: zodResolver(creditCheckFormSchema(formatMessage, kind)),
      mode: 'onBlur',
      defaultValues: {
        firstPartForm: {
          numberOfPeople: undefined,
          numberOfChildren: undefined,
          monthlyCosts: undefined,
          typeOfPropertyOwnership: undefined,
          loanFromFamily: undefined,
          studentLoan: undefined,
          otherLoans: undefined,
          monthlyLoanCosts: undefined,
        },
        secondPartForm: {
          automobileFollowUpQuestion: undefined,
          automobileQuestion: undefined,
          savingsRetirementQuestion: undefined,
          childDayCareQuestion: undefined,
          monthlyIncome: undefined,
        },
        wontDiscloseInformation: false,
      },
    });

  const handleDialogOpen: OpenDialogHandler = (
    dialogTitle: MessageDescriptor,
    dialogContent: MessageDescriptor,
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    showButtons?: boolean,
  ) => {
    e?.preventDefault();
    setDialogState({
      dialogTitle: formatMessage(dialogTitle),
      dialogContent: formatMessage(dialogContent, {
        break: <br />,
        strong: (...chunks: React.ReactNode[]) => <strong>{chunks}</strong>,
      }),
      open: true,
      showButtons: showButtons ?? false,
    });
  };
  const handleDialogClose = ({ agreedToContinue }: { agreedToContinue: boolean }) => {
    setDialogState({ dialogContent: '', dialogTitle: '', open: false });
    setValue('wontDiscloseInformation', !agreedToContinue);
  };
  const displayDisclosureError = () => {
    const wontDiscloseInformation = creditCheckWontDiscloseInformationSchema(formatMessage).parse(
      getValues('wontDiscloseInformation'),
    );
    // we only want to submit if the user is willing to disclose the information bring up a dialog
    if (wontDiscloseInformation === true) {
      handleDialogOpen(
        messages.dataCollectionDialogTitle,
        messages.dataCollectionDialogContent,
        undefined,
        true,
      );
    }
    return wontDiscloseInformation;
  };
  const onSubmitFirstFormHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isDialogDisplayed = displayDisclosureError();
    const triggered = await trigger('firstPartForm');
    const isFormStepOneValid = !isDialogDisplayed && triggered && !formState.errors.firstPartForm;
    isFormStepOneValid && setFormStep(2);
  };
  const onSubmitSecondFormHandler = async () => {
    const isDialogDisplayed = displayDisclosureError();
    const triggered = await trigger('secondPartForm');
    const isFormStepTwoValid = !isDialogDisplayed && triggered && !formState.errors.secondPartForm;
    if (isFormStepTwoValid) {
      const spreadOfValues: TiltFormSchema = {
        wontDiscloseInformation: getValues('wontDiscloseInformation'),
        ...getValues('firstPartForm'),
        monthlyIncome: getValues('secondPartForm.monthlyIncome'),
        automobileQuestion: getValues('secondPartForm.automobileQuestion'),
        savingsRetirementQuestion: getValues('secondPartForm.savingsRetirementQuestion'),
        automobileFollowUpQuestion: getValues('secondPartForm.automobileFollowUpQuestion'),
        childDayCareQuestion: getValues('secondPartForm.childDayCareQuestion'),
      };
      try {
        const response = await fetch(returnUrl, {
          method: 'POST',
          body: JSON.stringify(spreadOfValues),
        });

        if (response.redirected ?? response.status === 302) {
          window.location.href = response.url;
        }
      } catch (e) {
        // handle your error
      }
    }
  };
  const onSubmitFormHandler =
    formStep === 1 ? onSubmitFirstFormHandler : handleSubmit(onSubmitSecondFormHandler);

  return (
    <FormProvider
      {...{
        setValue,
        trigger,
        getValues,
        control,
        formState,
        handleSubmit,
        ...restMethods,
      }}
    >
      <form className="flex flex-col gap-1.5" onSubmit={onSubmitFormHandler}>
        <div className="bg-color-grey-light flex flex-col gap-1.5 p-1.5 md:gap-2 md:px-[44px] md:py-3">
          {formStep === 1 && (
            <>
              <h3 className="text-size-body1 font-bold">
                {formatMessage(messages.creditAgreementInfoHeadline)}
              </h3>
              <p className="text-size-body2">{formatMessage(messages.creditAgreementInfoText)}</p>
              <Button
                onClick={(e) =>
                  handleDialogOpen(messages.bkrDialogTitle, messages.bkrDialogText, e)
                }
                variant="link"
                size="base"
                className="text-size-body2 items-center justify-start self-start"
              >
                {formatMessage(messages.bkrInfoLink)}
              </Button>
              <RequiredHintParagraph />
              <LivingSituationCard />
              <AdditionalCostsCard />
            </>
          )}
          {formStep === 2 && (
            <>
              <RequiredHintParagraph />
              <FurtherInformationCard>
                <ShortFormSection kind={kind} handleOpenDialog={handleDialogOpen} />
                {kind === 'long' && <LongFormSection handleOpenDialog={handleDialogOpen} />}
              </FurtherInformationCard>
            </>
          )}
        </div>
        <Controller
          name="wontDiscloseInformation"
          control={control}
          render={({ field }) => (
            <div className="bg-color-background-base flex items-center p-1.5">
              <Checkbox
                onCheckedChange={field.onChange}
                checked={field.value}
                {...field}
                className="mr-2"
                value="wontDiscloseInformation"
                id="wontDiscloseInformation"
              />
              <Label className="leading-none" htmlFor="wontDiscloseInformation">
                {formatMessage(messages.wontDiscloseInformation)}
              </Label>
            </div>
          )}
        />
        <div className="bg-color-background-base flex px-1.5 py-0.5 md:justify-end md:px-0">
          <Button type="submit" className="w-full md:w-[12.5rem]" variant="primary" size="large">
            {formatMessage(messages.nextButton)}
          </Button>
        </div>
      </form>
      <Dialog
        handleClose={() => handleDialogClose({ agreedToContinue: false })}
        open={dialogState.open}
        title={isReactNodeString(dialogState.dialogTitle) ? dialogState.dialogTitle : ''}
        dialogContent={<div className="p-2">{dialogState.dialogContent}</div>}
        dialogActions={
          dialogState.showButtons && (
            <div className="flex w-full justify-between">
              <Button variant="default" asChild>
                <Link href="/kasse/zahlung" color="inherit" underline="none">
                  {formatMessage(messages.returnButton)}
                </Link>
              </Button>
              <Button
                onClick={() => handleDialogClose({ agreedToContinue: true })}
                variant="primary"
              >
                {formatMessage(messages.continueWithFormButton)}
              </Button>
            </div>
          )
        }
      />
    </FormProvider>
  );
};
